import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async'; // react-helmet yerine react-helmet-async kullanın
import { toast } from 'react-hot-toast';
import { getArticleBySlug, incrementArticleViewCount } from '../../services/firebase/blogService';
import Footer from '../../components/Footer';

const ArticleView = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadArticle = async () => {
      if (!slug) return;
      
      try {
        setLoading(true);
        const articleData = await getArticleBySlug(slug);
        
        // Yayınlanmamış makaleler sadece admin panelinden görüntülenebilir
        if (!articleData.isPublished) {
          navigate('/egitim');
          toast.error('Bu makale henüz yayınlanmamış');
          return;
        }
        
        setArticle(articleData);
        
        // Görüntüleme sayısını artır
        incrementArticleViewCount(articleData.id).catch(console.error);
        
      } catch (error) {
        console.error('Makale yüklenirken hata:', error);
        navigate('/egitim');
        toast.error('Makale bulunamadı');
      } finally {
        setLoading(false);
      }
    };

    loadArticle();
  }, [slug, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-800 text-gray-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (!article) {
    return null;
  }

  const { 
    title, 
    description, 
    content, 
    imageUrl, 
    keywords, 
    sections,
    createdAt,
    updatedAt,
    viewCount,
   
    categoryId 
  } = article;
  
  // Site URL'nizi buraya girin
  const siteUrl = "https://smartportfoy.com";
  const canonicalUrl = `${siteUrl}/egitim/${slug}`;
  
  // Tarihi formatlama yardımcı fonksiyonu
  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toISOString();
  };
  
  // Schema.org JSON-LD yapılandırması
  const schemaData = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": title,
    "description": description,
    "image": imageUrl ? imageUrl : `${siteUrl}/splogo_192_p.png`,
    "author": {
      "@type": "Person",
      "name": article.author || "Smart Portfoy"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Smart Portfoy",
      "logo": {
        "@type": "ImageObject",
        "url": `${siteUrl}/splogo_192_p.png`
      }
    },
    "datePublished": formatDate(createdAt),
    "dateModified": formatDate(updatedAt || createdAt),
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": canonicalUrl
    },
    "articleSection": categoryId || "Finans Eğitimi"
  };

  return (
    <>
      <Helmet>
        <title>{title} | Smart Portfoy</title>
        <meta name="description" content={description} />
        
        {/* Temel meta etiketleri */}
        <meta name="robots" content="index, follow" />
        {keywords && <meta name="keywords" content={keywords} />}
        <link rel="canonical" href={canonicalUrl} />
        
        {/* Open Graph meta etiketleri */}
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={canonicalUrl} />
        {imageUrl && <meta property="og:image" content={imageUrl} />}
        <meta property="og:site_name" content="Smart Portfoy" />
        <meta property="article:published_time" content={formatDate(createdAt)} />
        {updatedAt && <meta property="article:modified_time" content={formatDate(updatedAt)} />}
        <meta property="article:section" content={categoryId || "Finans Eğitimi"} />
        {keywords && <meta property="article:tag" content={keywords.split(',')[0]} />}
        
        {/* Twitter Card meta etiketleri */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
        {imageUrl && <meta name="twitter:image" content={imageUrl} />}
        
        {/* Yapılandırılmış veri - Schema.org */}
        <script type="application/ld+json">
          {JSON.stringify(schemaData)}
        </script>
      </Helmet>

      <div className="min-h-screen bg-gray-800 text-gray-100">
        {/* Hero Section */}
        <header className="bg-gradient-to-r from-blue-900 to-indigo-900 py-16">
          <div className="container mx-auto px-4 max-w-6xl">
            <h1 className="text-4xl font-bold mb-4 mt-8 bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-indigo-400">
              {title}
            </h1>
            <p className="text-lg text-gray-300 max-w-3xl leading-relaxed">
              {description}
            </p>
            
            {/* Yazar ve tarih bilgisi */}
            <div className="flex items-center text-gray-400 mt-4 text-sm">
              <span>{article.author || "Smart Portfoy"}</span>
              <span className="mx-2">•</span>
              <span>
                {createdAt?.toDate ? new Date(createdAt.toDate()).toLocaleDateString('tr-TR') : ''}
              </span>
              {viewCount && (
                <>
                  <span className="mx-2">•</span>
                  <span>{viewCount} görüntülenme</span>
                </>
              )}
            </div>
          </div>
        </header>

        {/* Image Section with Gradient Overlay */}
        {imageUrl && (
          <div className="relative h-48 md:h-64 lg:h-80">
            <div className="absolute inset-0 bg-gradient-to-b from-gray-900/50 to-gray-900 z-10"></div>
            <img 
              src={imageUrl} 
              alt={title}
              className="w-full h-full object-cover"
            />
          </div>
        )}

        {/* Content Section */}
        <div className="container mx-auto px-4 max-w-6xl py-8">
          {/* Rendered HTML Content */}
          {content && (
            <div className="prose prose-invert max-w-none mb-12" dangerouslySetInnerHTML={{ __html: content }}></div>
          )}
          
          {/* Sections */}
          <div className="space-y-8">
            {sections && sections.length > 0 && sections.map((section, index) => (
              <section key={index} className="space-y-4">
                <h2 className="text-2xl font-bold mb-4 text-blue-400 no-underline">
                  {section.title}
                </h2>
                {section.description && (
                  <p className="text-gray-300 text-base">{section.description}</p>
                )}
                {section.points && section.points.length > 0 && (
                  <ul className="space-y-2">
                    {section.points.map((point, pIndex) => (
                      <li key={pIndex} className="flex items-start space-x-2">
                        <span className="flex-shrink-0 w-6 h-6 bg-indigo-500 rounded-full flex items-center justify-center text-gray-900 font-bold">
                          {pIndex + 1}
                        </span>
                        <span className="text-gray-300">{point}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            ))}
          </div>
          
          {/* Anahtar kelimeler ve etiketler */}
          {keywords && (
            <div className="mt-8 pt-4 border-t border-gray-700">
              <h3 className="text-lg font-medium text-gray-300 mb-3">Etiketler:</h3>
              <div className="flex flex-wrap gap-2">
                {keywords.split(',').map((tag, index) => (
                  <span 
                    key={index} 
                    className="bg-gray-700 text-gray-200 px-3 py-1 rounded-full text-sm"
                  >
                    {tag.trim()}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        <Footer />
      </div>
    </>
  );
};

export default ArticleView;